import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import {
  useLatestNotifications,
  useMarkNotificationRead,
  useUnreadNotifications,
} from '../../api/hooks';
import { t } from '../../i18n/sq';
import { NotificationList, NotificationsEmpty } from './NotificationList';

/** Bell in the top bar: the number of new notifications, and the latest ones in a panel. */
export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data: unread = 0 } = useUnreadNotifications();
  const latest = useLatestNotifications(!!anchor);
  const markRead = useMarkNotificationRead();
  const close = () => setAnchor(null);
  const label = t.notifications.bell(unread);

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={!!anchor}
        >
          <Badge badgeContent={unread} max={99} color="error" aria-hidden>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': t.notifications.title,
            sx: { width: 400, maxWidth: 'calc(100vw - 32px)' },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h6" component="h2">
            {t.notifications.title}
          </Typography>
          {unread > 0 && (
            <Button size="small" onClick={() => markRead.mutate('all')}>
              {t.notifications.markAllRead}
            </Button>
          )}
        </Box>
        <Divider />
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {latest.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} aria-label={t.common.loading} />
            </Box>
          ) : latest.data?.items.length ? (
            <NotificationList items={latest.data.items} onOpen={close} dense />
          ) : (
            <NotificationsEmpty />
          )}
        </Box>
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button component={RouterLink} to="/njoftimet" onClick={close} fullWidth>
            {t.notifications.seeAll}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
